/**
 * What the user had open when they sent a message from the Alpinist side panel
 * (AB#6762, layer 1). References only: ids, a time window and view parameters,
 * never values or free page text. The backend renders it as a `[Screen]` block in
 * the turn's prompt and decides itself whether the message refers to it.
 */

export const PAGE_TYPES = [
  'signal_explorer',
  'custom_dashboard',
  'line_dashboard',
  'equipment_dashboard',
  'incident',
  'batch_traceability',
  'settings',
  'other',
] as const

export type PageType = (typeof PAGE_TYPES)[number]

// Null and undefined members are dropped when the context is read, so callers
// can pass their nullable fields straight through.
export type PageContextEntities = {
  line_id?: string | null
  equipment_id?: string | null
  dashboard_id?: string | null
  incident_id?: string | null
  program_id?: string | null
  batch_ids?: string[]
  signal_ids?: string[]
}

export type PageContextTimeRange = {
  /** ISO-8601. */
  start?: string | null
  /** ISO-8601. */
  end?: string | null
  /** A relative preset such as `last_24h`, or null for a fixed window. */
  preset?: string | null
}

export type PageContextView = {
  tab?: string
  mode?: string
  settings_section?: string
}

export type PageContextV1 = {
  v: 1
  page: PageType
  path: string
  entities?: PageContextEntities
  time_range?: PageContextTimeRange
  view?: PageContextView
  /** ISO-8601, set when the message is sent. */
  captured_at: string
}

/**
 * What a page publishes: everything but the envelope, which the registry fills
 * in (`v`, `path`, `captured_at`). Merged over the route fallback, so a page only
 * names what it knows better than the URL.
 */
export type PublishedPageContext = {
  page?: PageType
  entities?: PageContextEntities
  time_range?: PageContextTimeRange
  view?: PageContextView
}
